// src/core/financial-engine/money.ts
// ─────────────────────────────────────────────────────────────────────────────
// NIIZ ERP · Núcleo Financiero · Aritmética Monetaria Determinista
// ─────────────────────────────────────────────────────────────────────────────
// Módulo PURO: cero dependencias externas, cero efectos secundarios.
// Todo valor monetario es `bigint` en centavos. Nunca se usa `number`
// para aritmética de dinero (IEEE 754 produce errores de redondeo).
//
// Reglas de oro:
//   1. La unidad canónica es el CENTAVO (bigint).
//   2. Los porcentajes se expresan en BASIS POINTS (10 000 bps = 100 %).
//   3. Todo reparto usa Hamilton / Mayor Resto → Σ partes ≡ total.
//   4. El redondeo por defecto es HALF_EVEN (bancario). Para IVA/fiscal
//      se usa HALF_UP explícitamente.
//   5. En la frontera de persistencia (IndexedDB) se convierte a `number`
//      con guardia `Number.isSafeInteger` (tope ≈ 90 billones de COP).
// ─────────────────────────────────────────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════════════════
// 1. TIPO CANÓNICO BRANDED
// ═══════════════════════════════════════════════════════════════════════════

/** Symbol único que hace imposible asignar un `bigint` plano a `Centavos`. */
declare const brand: unique symbol;

/**
 * Unidad canónica de moneda: **centavos** en `bigint`.
 * El branded type impide mezclar accidentalmente centavos con otros bigints.
 *
 * ```ts
 * const precio = C(125_050n); // 1 250,50 en centavos
 * ```
 */
export type Centavos = bigint & { readonly [brand]: 'Centavos' };

/** Basis points: 10 000 = 100 %. Se usa bigint para no perder precisión. */
export type Bps = bigint;

/** Modos de redondeo soportados por `divRound`. */
export type Rounding = 'HALF_EVEN' | 'HALF_UP' | 'FLOOR';

/** Constructor seguro: envuelve un `bigint` como `Centavos`. */
export const C = (n: bigint): Centavos => n as Centavos;

/** Constructor desde basis points numéricos. */
export const BPS = (n: number): Bps => BigInt(n);

/** Cero monetario. */
export const ZERO: Centavos = C(0n);

/** 100 % en basis points. */
export const BPS_100: Bps = 10_000n;

// ═══════════════════════════════════════════════════════════════════════════
// 2. PARSING TOLERANTE A FORMATOS INTERNACIONALES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Opciones de formato para `formatMoney`.
 * Todas son opcionales; los defaults producen formato latino colombiano.
 */
export interface FormatMoneyOptions {
  /** Símbolo monetario (ej: `$`, `€`, `US$`). Default: `'$'`. */
  currencySymbol?: string;
  /** Separador decimal. Default: `','`. */
  decimalSeparator?: string;
  /** Separador de miles. Default: `'.'`. */
  thousandSeparator?: string;
  /** Si `true`, omite el símbolo de moneda. Default: `false`. */
  omitSymbol?: boolean;
}

/**
 * Interpreta un string monetario en formato **latino** (`1.250,50`),
 * **anglosajón** (`1,250.50`) o **entero puro** (`1250`) y retorna
 * el valor en `Centavos` (× 100).
 *
 * Reglas de desambiguación:
 * - Si existen **ambos** separadores (coma y punto), el que aparece
 *   **último** es el decimal; el otro es de miles.
 * - Si hay **un solo tipo** de separador:
 *   - Aparece **más de una vez** → es de miles.
 *   - Aparece **una vez** con **1-2 dígitos** después → es decimal.
 *   - Aparece **una vez** con **3+ dígitos** después → es de miles.
 *
 * @throws {RangeError} Si el string no contiene un número válido.
 *
 * @example
 * parseMoney('1.250,50')   → C(125_050n)
 * parseMoney('1,250.50')   → C(125_050n)
 * parseMoney('$1250')      → C(125_000n)
 * parseMoney('-0,99')      → C(-99n)
 */
export function parseMoney(input: string): Centavos {
  if (typeof input !== 'string') {
    throw new RangeError(`parseMoney: se esperaba string, se recibió ${typeof input}`);
  }

  // 1. Limpiar: eliminar símbolos de moneda, espacios, signos de agrupación
  //    no numéricos. Conservar dígitos, comas, puntos y signo negativo.
  let s = input.trim();

  // Extraer y retirar el signo negativo (puede estar antes o después del símbolo)
  let negative = false;
  if (s.startsWith('-')) {
    negative = true;
    s = s.slice(1);
  } else if (s.startsWith('(') && s.endsWith(')')) {
    // Formato contable: (1.250,50) = negativo
    negative = true;
    s = s.slice(1, -1);
  }

  // Eliminar todo lo que no sea dígito, coma o punto
  s = s.replace(/[^0-9.,]/g, '');

  if (s.length === 0) {
    throw new RangeError(`parseMoney: cadena vacía o sin dígitos: "${input}"`);
  }

  // 2. Determinar qué separador es el decimal
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  const commaCount = (s.match(/,/g) ?? []).length;
  const dotCount = (s.match(/\./g) ?? []).length;

  let decimalSep: ',' | '.' | null = null;
  let thousandSep: ',' | '.' | null = null;

  if (lastComma >= 0 && lastDot >= 0) {
    // Ambos existen → el ÚLTIMO es decimal
    if (lastComma > lastDot) {
      decimalSep = ',';
      thousandSep = '.';
    } else {
      decimalSep = '.';
      thousandSep = ',';
    }
  } else if (lastComma >= 0) {
    // Solo comas
    if (commaCount > 1) {
      thousandSep = ','; // Múltiples comas → miles
    } else {
      // Una sola coma: ¿decimal o miles?
      const afterComma = s.length - lastComma - 1;
      decimalSep = afterComma <= 2 ? ',' : null;
      thousandSep = afterComma <= 2 ? null : ',';
    }
  } else if (lastDot >= 0) {
    // Solo puntos
    if (dotCount > 1) {
      thousandSep = '.'; // Múltiples puntos → miles
    } else {
      const afterDot = s.length - lastDot - 1;
      decimalSep = afterDot <= 2 ? '.' : null;
      thousandSep = afterDot <= 2 ? null : '.';
    }
  }
  // Si no hay separadores → entero puro

  // 3. Construir el número limpio
  if (thousandSep !== null) {
    s = s.split(thousandSep).join('');
  }
  if (decimalSep !== null) {
    s = s.replace(decimalSep, '.');
  }

  // 4. Separar parte entera y decimal
  const dotIdx = s.indexOf('.');
  let intPart: string;
  let decPart: string;

  if (dotIdx >= 0) {
    intPart = s.slice(0, dotIdx);
    decPart = s.slice(dotIdx + 1);
  } else {
    intPart = s;
    decPart = '';
  }

  // Validar que solo queden dígitos
  if (intPart.length === 0 && decPart.length === 0) {
    throw new RangeError(`parseMoney: sin dígitos válidos: "${input}"`);
  }
  if (intPart.length > 0 && !/^\d+$/.test(intPart)) {
    throw new RangeError(`parseMoney: parte entera inválida: "${intPart}"`);
  }
  if (decPart.length > 0 && !/^\d+$/.test(decPart)) {
    throw new RangeError(`parseMoney: parte decimal inválida: "${decPart}"`);
  }

  // 5. Normalizar a exactamente 2 dígitos decimales (centavos)
  if (decPart.length === 0) {
    decPart = '00';
  } else if (decPart.length === 1) {
    decPart = decPart + '0';
  } else if (decPart.length > 2) {
    // Truncar a 2 dígitos (no redondear: el parsing preserva lo escrito)
    decPart = decPart.slice(0, 2);
  }

  const intBig = intPart.length > 0 ? BigInt(intPart) : 0n;
  const decBig = BigInt(decPart);
  const total = intBig * 100n + decBig;

  return C(negative ? -total : total);
}

/**
 * Formatea `Centavos` como string legible con separadores configurables.
 *
 * @example
 * formatMoney(C(125_050n))                          → '$1.250,50'
 * formatMoney(C(125_050n), { currencySymbol: '€' }) → '€1.250,50'
 * formatMoney(C(-99n), { omitSymbol: true })         → '-0,99'
 */
export function formatMoney(c: Centavos, options?: FormatMoneyOptions): string {
  const symbol = options?.currencySymbol ?? '$';
  const decSep = options?.decimalSeparator ?? ',';
  const thouSep = options?.thousandSeparator ?? '.';
  const omitSym = options?.omitSymbol ?? false;

  const negative = c < 0n;
  const abs = negative ? -c : c;

  const intPart = abs / 100n;
  const decPart = abs % 100n;

  // Formatear la parte entera con separador de miles
  let intStr = intPart.toString();
  // Insertar separadores de miles de derecha a izquierda
  const parts: string[] = [];
  while (intStr.length > 3) {
    parts.unshift(intStr.slice(-3));
    intStr = intStr.slice(0, -3);
  }
  parts.unshift(intStr);
  const formattedInt = parts.join(thouSep);

  // Parte decimal: siempre 2 dígitos
  const formattedDec = decPart.toString().padStart(2, '0');

  const sign = negative ? '-' : '';
  const prefix = omitSym ? '' : symbol;

  return `${sign}${prefix}${formattedInt}${decSep}${formattedDec}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// 3. CONVERSIÓN BIDIRECCIONAL CON ALMACENAMIENTO (IndexedDB / JSON)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Máximo valor seguro: `Number.MAX_SAFE_INTEGER` = 9 007 199 254 740 991.
 * En centavos eso equivale a ≈ 90 071 992 547 409 COP (90 billones).
 */
const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);
const MIN_SAFE = BigInt(Number.MIN_SAFE_INTEGER);

/**
 * Convierte `Centavos` → `number` entero para persistir en IndexedDB.
 * IndexedDB no indexa `bigint`, así que se almacena como `number`.
 *
 * @throws {RangeError} Si el valor excede `Number.MAX_SAFE_INTEGER`.
 */
export function toStorage(c: Centavos): number {
  if (c > MAX_SAFE || c < MIN_SAFE) {
    throw new RangeError(
      `toStorage: valor ${c} fuera del rango seguro de Number ` +
      `(±${MAX_SAFE}). Use serialización string para montos extremos.`,
    );
  }
  return Number(c);
}

/**
 * Reconstruye `Centavos` desde un `number` leído de IndexedDB.
 * Valida que el número sea un entero seguro (integridad del dato).
 *
 * @throws {RangeError} Si el valor no es un entero seguro.
 */
export function fromStorage(n: number): Centavos {
  if (!Number.isSafeInteger(n)) {
    throw new RangeError(
      `fromStorage: el valor ${n} no es un entero seguro. ` +
      `Posible corrupción de datos o desbordamiento.`,
    );
  }
  return C(BigInt(n));
}

/**
 * Serializa `Centavos` como string decimal para JSON o transporte.
 * Nunca pierde precisión (a diferencia de `Number()`).
 */
export function serialize(c: Centavos): string {
  return c.toString();
}

/**
 * Deserializa un string decimal a `Centavos`.
 *
 * @throws {RangeError} Si el string no es un entero válido.
 */
export function deserialize(s: string): Centavos {
  const trimmed = s.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    throw new RangeError(`deserialize: "${s}" no es un entero válido`);
  }
  return C(BigInt(trimmed));
}

// ═══════════════════════════════════════════════════════════════════════════
// 4. DIVISIÓN CON REDONDEO BANCARIO DETERMINISTA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Divide `n / d` con redondeo determinista.
 *
 * | Modo         | Empate (x.5)           | Uso típico            |
 * |--------------|------------------------|-----------------------|
 * | `HALF_EVEN`  | Redondea al par más cercano | Bancario / defecto |
 * | `HALF_UP`    | Redondea lejos de cero | Fiscal / IVA (DIAN)   |
 * | `FLOOR`      | Trunca hacia −∞        | Repartos conservadores|
 *
 * @throws {RangeError} Si `d === 0n` (protección absoluta).
 *
 * @example
 * divRound(5n, 2n)             → 2n   (HALF_EVEN: 2 es par)
 * divRound(7n, 2n)             → 4n   (HALF_EVEN: 4 es par)
 * divRound(5n, 2n, 'HALF_UP') → 3n   (lejos de cero)
 * divRound(-5n, 2n, 'FLOOR')  → -3n  (hacia −∞)
 */
export function divRound(
  n: bigint,
  d: bigint,
  mode: Rounding = 'HALF_EVEN',
): bigint {
  if (d === 0n) {
    throw new RangeError(
      `divRound: división por cero (numerador = ${n}, modo = ${mode}). ` +
      `Verifique el denominador antes de llamar.`,
    );
  }

  // Normalizar: el divisor siempre positivo (el signo lo lleva el numerador)
  if (d < 0n) {
    n = -n;
    d = -d;
  }

  const q = n / d;  // Trunca hacia cero (comportamiento estándar de bigint)
  const r = n % d;  // Resto con el signo de n

  if (r === 0n) return q;

  const isNeg = n < 0n;
  const absR = isNeg ? -r : r;
  const twice = absR * 2n;

  // ── FLOOR: siempre hacia −∞ ──
  if (mode === 'FLOOR') {
    return isNeg ? q - 1n : q;
  }

  // ── HALF_EVEN / HALF_UP ──
  if (twice < d) {
    // El resto es menor que la mitad → truncar (acercarse a cero)
    return q;
  }

  if (twice > d) {
    // El resto es mayor que la mitad → alejarse de cero
    return isNeg ? q - 1n : q + 1n;
  }

  // ── Empate exacto: |resto| × 2 === |divisor| ──
  if (mode === 'HALF_UP') {
    // Siempre alejarse de cero
    return isNeg ? q - 1n : q + 1n;
  }

  // HALF_EVEN: elegir el candidato PAR
  // Para n > 0: q es el piso, q+1 el techo → elegir el par
  // Para n < 0: q es el techo (hacia cero), q-1 el piso → elegir el par
  if (isNeg) {
    return q % 2n === 0n ? q : q - 1n;
  }
  return q % 2n === 0n ? q : q + 1n;
}

// ═══════════════════════════════════════════════════════════════════════════
// 5. ALGORITMO DE HAMILTON / MAYOR RESTO (Reparto Proporcional Exacto)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Distribuye `total` proporcionalmente según `weights` usando el método
 * de **Hamilton** (Mayor Resto / Largest Remainder).
 *
 * **Garantía matemática:** `Σ allocate(total, weights) === total` SIEMPRE.
 * No se pierde ni se crea ni un centavo.
 *
 * **Desempate determinista:** cuando dos pesos tienen el mismo resto,
 * se asigna al de **menor índice** primero. Esto elimina cualquier
 * variación arbitraria entre ejecuciones.
 *
 * @param total   Monto total a distribuir (puede ser negativo).
 * @param weights Pesos relativos (deben sumar > 0). No necesitan sumar 10 000.
 * @returns Array de `Centavos` con la misma longitud que `weights`.
 *
 * @throws {RangeError} Si `weights` está vacío o suma ≤ 0.
 *
 * @example
 * // Regla 50/20/15/15
 * allocate(C(1_000_000n), [5000n, 2000n, 1500n, 1500n])
 * // → [C(500_000n), C(200_000n), C(150_000n), C(150_000n)]
 *
 * // Caso con residuo: 10 centavos entre 3 partes iguales
 * allocate(C(10n), [1n, 1n, 1n])
 * // → [C(4n), C(3n), C(3n)]  (el centavo extra va al índice 0)
 */
export function allocate(total: Centavos, weights: Bps[]): Centavos[] {
  if (weights.length === 0) return [];

  const W = weights.reduce((a, b) => a + b, 0n);
  if (W <= 0n) {
    throw new RangeError(
      `allocate: la suma de pesos debe ser > 0, se obtuvo ${W}`,
    );
  }

  // Trabajar con el valor absoluto; el signo se aplica al final.
  const sign = total < 0n ? -1n : 1n;
  const t = sign === 1n ? total : -total;

  // Paso 1: asignación base (piso de la división proporcional)
  const base: bigint[] = weights.map((w) => (t * w) / W);
  let remainder = t - base.reduce((a, b) => a + b, 0n);

  // Paso 2: calcular los restos fraccionarios de cada peso
  const indexed: Array<{ index: number; rest: bigint }> = weights.map(
    (w, i) => ({
      index: i,
      rest: (t * w) % W,
    }),
  );

  // Paso 3: ordenar por resto DESCENDENTE; empate → menor índice primero
  indexed.sort((a, b) => {
    if (a.rest > b.rest) return -1;
    if (a.rest < b.rest) return 1;
    return a.index - b.index; // desempate determinista por índice
  });

  // Paso 4: distribuir los centavos restantes uno a uno
  const result = [...base];
  for (let k = 0; remainder > 0n; k++, remainder--) {
    result[indexed[k].index] += 1n;
  }

  // Paso 5: aplicar el signo original
  return result.map((x) => C(x * sign));
}

/**
 * Verifica que un reparto de Hamilton es correcto: la suma de las partes
 * debe ser exactamente igual al total. Útil para tests y aserciones.
 */
export function verifyAllocation(total: Centavos, parts: Centavos[]): boolean {
  const sum = parts.reduce((a, b) => a + b, 0n);
  return sum === total;
}

// ═══════════════════════════════════════════════════════════════════════════
// 6. MULTIPLICACIÓN POR BASIS POINTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Multiplica un monto por una tasa en **basis points** con redondeo
 * determinista.
 *
 * 19 % = 1 900 bps · 100 % = 10 000 bps · 0.5 % = 50 bps
 *
 * @param a    Monto base.
 * @param bps  Tasa en basis points (bigint).
 * @param mode Modo de redondeo. Default: `HALF_EVEN`. Para IVA usar `HALF_UP`.
 * @returns El monto proporcional redondeado a centavos enteros.
 *
 * @example
 * mulBps(C(100_000n), 1900n) // 19 % de 1 000,00 → C(19_000n)
 */
export function mulBps(
  a: Centavos,
  bps: Bps,
  mode: Rounding = 'HALF_EVEN',
): Centavos {
  return C(divRound(a * bps, BPS_100, mode));
}

/**
 * Calcula el **complemento** de una tasa en bps.
 * Útil para extraer la base de un precio con IVA incluido:
 * `base = mulBpsInverse(price, 1900n)` → `price / (1 + 0.19)`
 *
 * @param a   Monto total (con impuesto incluido).
 * @param bps Tasa del impuesto en bps.
 * @param mode Modo de redondeo.
 */
export function mulBpsInverse(
  a: Centavos,
  bps: Bps,
  mode: Rounding = 'HALF_EVEN',
): Centavos {
  const denominator = BPS_100 + bps;
  return C(divRound(a * BPS_100, denominator, mode));
}

// ═══════════════════════════════════════════════════════════════════════════
// 7. UTILIDADES ARITMÉTICAS ADICIONALES
// ═══════════════════════════════════════════════════════════════════════════

/** Suma de un array de `Centavos`. */
export function sumCentavos(values: ReadonlyArray<Centavos>): Centavos {
  return C(values.reduce((acc, v) => acc + v, 0n));
}

/** Valor absoluto. */
export function absCentavos(c: Centavos): Centavos {
  return c < 0n ? C(-c) : c;
}

/** Máximo entre dos valores monetarios. */
export function maxCentavos(a: Centavos, b: Centavos): Centavos {
  return a >= b ? a : b;
}

/** Mínimo entre dos valores monetarios. */
export function minCentavos(a: Centavos, b: Centavos): Centavos {
  return a <= b ? a : b;
}

/** Comparación segura. */
export function isZero(c: Centavos): boolean {
  return c === 0n;
}

/** Verifica si un monto es positivo (> 0). */
export function isPositive(c: Centavos): boolean {
  return c > 0n;
}

/** Verifica si un monto es negativo (< 0). */
export function isNegative(c: Centavos): boolean {
  return c < 0n;
}

/**
 * Divide un monto entre `n` partes iguales usando Hamilton.
 * Garantiza que la suma de las partes === total.
 */
export function splitEqually(total: Centavos, n: number): Centavos[] {
  if (n <= 0) throw new RangeError(`splitEqually: n debe ser > 0, se recibió ${n}`);
  const weights: Bps[] = Array.from({ length: n }, () => 1n);
  return allocate(total, weights);
}

// ═══════════════════════════════════════════════════════════════════════════
// 8. PRUEBAS DE ASERCIÓN INTERNA (self-test)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ejecuta la batería completa de pruebas internas del módulo.
 * Llama esta función en el arranque de la app (dev) o en CI.
 *
 * @throws {Error} Si alguna aserción falla.
 * @returns Número de aserciones verificadas.
 */
export function runMoneySelfTest(): number {
  let passed = 0;

  function assert(condition: boolean, label: string): void {
    if (!condition) {
      throw new Error(`[money.ts SELF-TEST FAIL] ${label}`);
    }
    passed++;
  }

  // ── parseMoney ──
  assert(parseMoney('1.250,50') === 125_050n, 'parseMoney latino');
  assert(parseMoney('1,250.50') === 125_050n, 'parseMoney anglosajón');
  assert(parseMoney('1250') === 125_000n, 'parseMoney entero puro');
  assert(parseMoney('$1250') === 125_000n, 'parseMoney con símbolo');
  assert(parseMoney('-0,99') === -99n, 'parseMoney negativo decimal');
  assert(parseMoney('0.50') === 50n, 'parseMoney 0.50');
  assert(parseMoney('0,50') === 50n, 'parseMoney 0,50');
  assert(parseMoney('1.250') === 125_000n, 'parseMoney miles con punto');
  assert(parseMoney('1,250') === 125_000n, 'parseMoney miles con coma');
  assert(parseMoney('99,9') === 9_990n, 'parseMoney un decimal');
  assert(parseMoney('(500)') === -50_000n, 'parseMoney formato contable negativo');

  // parseMoney errores
  let threw = false;
  try { parseMoney(''); } catch { threw = true; }
  assert(threw, 'parseMoney vacío lanza error');

  threw = false;
  try { parseMoney('abc'); } catch { threw = true; }
  assert(threw, 'parseMoney no numérico lanza error');

  // ── formatMoney ──
  assert(formatMoney(C(125_050n)) === '$1.250,50', 'formatMoney latino');
  assert(formatMoney(C(-99n)) === '-$0,99', 'formatMoney negativo');
  assert(formatMoney(C(0n)) === '$0,00', 'formatMoney cero');
  assert(
    formatMoney(C(1_234_567_89n), { thousandSeparator: ',', decimalSeparator: '.' }) ===
    '$1,234,567.89',
    'formatMoney anglosajón',
  );
  assert(
    formatMoney(C(500n), { omitSymbol: true }) === '5,00',
    'formatMoney sin símbolo',
  );

  // ── toStorage / fromStorage round-trip ──
  const testVal = C(9_007_199_254_740_991n); // MAX_SAFE_INTEGER
  assert(fromStorage(toStorage(testVal)) === testVal, 'toStorage/fromStorage round-trip max');

  const negVal = C(-123_456n);
  assert(fromStorage(toStorage(negVal)) === negVal, 'toStorage/fromStorage round-trip negativo');

  threw = false;
  try { toStorage(C(MAX_SAFE + 1n)); } catch { threw = true; }
  assert(threw, 'toStorage desbordamiento lanza error');

  threw = false;
  try { fromStorage(1.5); } catch { threw = true; }
  assert(threw, 'fromStorage no entero lanza error');

  threw = false;
  try { fromStorage(Number.MAX_SAFE_INTEGER + 100); } catch { threw = true; }
  assert(threw, 'fromStorage fuera de rango lanza error');

  // ── divRound ──
  assert(divRound(10n, 3n) === 3n, 'divRound 10/3 → 3');
  assert(divRound(5n, 2n) === 2n, 'divRound HALF_EVEN 5/2 → 2 (par)');
  assert(divRound(7n, 2n) === 4n, 'divRound HALF_EVEN 7/2 → 4 (par)');
  assert(divRound(5n, 2n, 'HALF_UP') === 3n, 'divRound HALF_UP 5/2 → 3');
  assert(divRound(-5n, 2n, 'HALF_UP') === -3n, 'divRound HALF_UP -5/2 → -3');
  assert(divRound(-5n, 2n) === -2n, 'divRound HALF_EVEN -5/2 → -2 (par)');
  assert(divRound(-7n, 2n) === -4n, 'divRound HALF_EVEN -7/2 → -4 (par)');
  assert(divRound(5n, 2n, 'FLOOR') === 2n, 'divRound FLOOR 5/2 → 2');
  assert(divRound(-5n, 2n, 'FLOOR') === -3n, 'divRound FLOOR -5/2 → -3');
  assert(divRound(10n, 5n) === 2n, 'divRound exacto');
  assert(divRound(0n, 7n) === 0n, 'divRound cero numerador');

  threw = false;
  try { divRound(1n, 0n); } catch { threw = true; }
  assert(threw, 'divRound división por cero lanza error');

  // ── allocate (Hamilton) ──
  const alloc1 = allocate(C(1_000_000n), [5000n, 2000n, 1500n, 1500n]);
  assert(alloc1[0] === 500_000n, 'allocate 50%');
  assert(alloc1[1] === 200_000n, 'allocate 20%');
  assert(alloc1[2] === 150_000n, 'allocate 15%');
  assert(alloc1[3] === 150_000n, 'allocate 15%');
  assert(verifyAllocation(C(1_000_000n), alloc1), 'allocate suma exacta 50/20/15/15');

  // Caso con residuo: 10 entre 3
  const alloc2 = allocate(C(10n), [1n, 1n, 1n]);
  assert(
    alloc2[0] + alloc2[1] + alloc2[2] === 10n,
    'allocate 10/3 suma exacta',
  );
  assert(alloc2[0] === 4n, 'allocate 10/3: índice 0 recibe el extra');
  assert(alloc2[1] === 3n, 'allocate 10/3: índice 1');
  assert(alloc2[2] === 3n, 'allocate 10/3: índice 2');

  // Total cero
  const alloc3 = allocate(C(0n), [1n, 2n, 3n]);
  assert(alloc3.every((v) => v === 0n), 'allocate total cero');

  // Total negativo
  const alloc4 = allocate(C(-100n), [1n, 1n]);
  assert(alloc4[0] + alloc4[1] === -100n, 'allocate negativo suma exacta');

  // Un solo peso
  const alloc5 = allocate(C(999n), [1n]);
  assert(alloc5[0] === 999n, 'allocate un solo peso');

  // Pesos vacíos
  const alloc6 = allocate(C(100n), []);
  assert(alloc6.length === 0, 'allocate pesos vacíos');

  threw = false;
  try { allocate(C(100n), [0n, 0n]); } catch { threw = true; }
  assert(threw, 'allocate pesos que suman 0 lanza error');

  // Propiedad: para cualquier total y pesos, la suma siempre es exacta
  const stressTotal = C(12_345_678_901n);
  const stressWeights = [3333n, 3333n, 3334n];
  const stressAlloc = allocate(stressTotal, stressWeights);
  assert(
    verifyAllocation(stressTotal, stressAlloc),
    'allocate stress test: suma invariante',
  );

  // ── mulBps ──
  assert(mulBps(C(100_000n), 1900n) === 19_000n, 'mulBps 19%');
  assert(mulBps(C(100_000n), 10_000n) === 100_000n, 'mulBps 100%');
  assert(mulBps(C(100_000n), 0n) === 0n, 'mulBps 0%');
  assert(mulBps(C(33_333n), 5000n) === 16_666n, 'mulBps 50% de 333,33 → 166,66 (HALF_EVEN)');

  // mulBpsInverse: extraer base de precio con IVA
  const withIva = C(119_000n); // 100 000 + 19% IVA
  const base = mulBpsInverse(withIva, 1900n);
  assert(base === 100_000n, 'mulBpsInverse extrae base correcta');

  // ── splitEqually ──
  const split3 = splitEqually(C(100n), 3);
  assert(split3[0] + split3[1] + split3[2] === 100n, 'splitEqually 100/3 suma exacta');
  assert(split3[0] === 34n, 'splitEqually 100/3: primero recibe extra');

  threw = false;
  try { splitEqually(C(100n), 0); } catch { threw = true; }
  assert(threw, 'splitEqually n=0 lanza error');

  // ── sumCentavos ──
  assert(
    sumCentavos([C(10n), C(20n), C(30n)]) === 60n,
    'sumCentavos',
  );
  assert(sumCentavos([]) === 0n, 'sumCentavos vacío');

  // ── serialize / deserialize round-trip ──
  const serVal = C(-987_654_321n);
  assert(deserialize(serialize(serVal)) === serVal, 'serialize/deserialize round-trip');

  threw = false;
  try { deserialize('12.5'); } catch { threw = true; }
  assert(threw, 'deserialize no entero lanza error');

  return passed;
}

// ═══════════════════════════════════════════════════════════════════════════
// 9. AUTO-EJECUCIÓN EN DESARROLLO
// ═══════════════════════════════════════════════════════════════════════════

// En desarrollo, ejecutar las pruebas automáticamente al importar el módulo.
// En producción (NDEBUG / NODE_ENV=production) se omite para no afectar el
// rendimiento del arranque.
if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
  try {
    const count = runMoneySelfTest();
    console.log(`✅ [money.ts] ${count} aserciones internas verificadas correctamente.`);
  } catch (err) {
    console.error('🔴 [money.ts] FALLO en pruebas internas:', err);
  }
}